import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateSafely } from "../utils/safeNavigate";
import { formatBeijingDateTime } from "../utils/datetime";

export default function SystemMessagesPage({
  role = "user",
  storageKey = "user_id",
  theme = "pink",
}) {
  const navigate = useNavigate();
  const accountId = Number(localStorage.getItem(storageKey));
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const queryKey = role === "consultant" ? "consultant_id" : "user_id";
  const activeClass =
    theme === "blue"
      ? "bg-blue-50 border-blue-100"
      : "bg-pink-50 border-pink-100";
  const linkClass =
    theme === "blue" ? "text-blue-600 hover:underline" : "text-pink-600 hover:underline";

  const loadMessages = async () => {
    try {
      const res = await fetch(`/api/system-messages?${queryKey}=${accountId}`);
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("加载系统消息失败:", err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accountId) {
      navigate("/login");
      return;
    }
    loadMessages();
  }, [accountId, navigate]);

  const markRead = async (message) => {
    if (message.is_read) return;

    try {
      await fetch(
        `/api/system-messages/${message.id}/read?${queryKey}=${accountId}`,
        { method: "POST" }
      );
      window.dispatchEvent(new Event("systemMessageUpdated"));
      setMessages((prev) =>
        prev.map((item) =>
          item.id === message.id ? { ...item, is_read: 1 } : item
        )
      );
    } catch (err) {
      console.error("标记已读失败:", err);
    }
  };

  const handleOpen = async (message) => {
    await markRead(message);
    if (message.link_url) {
      navigateSafely(navigate, message.link_url);
    }
  };

  if (loading) {
    return <div className="text-gray-400">加载中...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">系统消息</h2>
        <p className="text-sm text-gray-400 mt-1">
          平台通知、认证提醒、AI 回访与运营消息
        </p>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-16 text-gray-400">暂无系统消息</div>
      ) : (
        <div className="space-y-4">
          {messages.map((item) => (
            <div
              key={item.id}
              className={`border rounded-2xl p-5 ${
                item.is_read ? "bg-white border-gray-100" : activeClass
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-800">{item.title}</h3>
                    {!item.is_read && (
                      <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
                        未读
                      </span>
                    )}
                    {item.category === "audit" && (
                      <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                        认证
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mt-2 whitespace-pre-wrap leading-7">
                    {item.content}
                  </p>
                  <p className="text-xs text-gray-400 mt-3">
                    {item.created_at ? formatBeijingDateTime(item.created_at) : ""}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex gap-3">
                {item.link_url && (
                  <button
                    type="button"
                    onClick={() => handleOpen(item)}
                    className={`text-sm font-medium ${linkClass}`}
                  >
                    前往处理 →
                  </button>
                )}
                {!item.is_read && (
                  <button
                    type="button"
                    onClick={() => markRead(item)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    标记已读
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
